import React from 'react';
import PropTypes from 'prop-types';
import { Skeleton, Table } from '@mantine/core';
import dayjs from 'dayjs';
import NumberFormat from 'react-number-format';
import AppFlexbox from '../../common/AppFlexbox';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import { WEBSITE_PAYMENT_STATUS_ENUM } from '../../../config/constants';
import {
  getInstallmentStatusInfo,
  PROCESSING_COLOR
} from '../../../helpers/customerPaymentsHelper';
import { formatLocalDate } from '../../../helpers/format';

const HEADER_STYLE = {
  padding: '8px 16px',
  backgroundColor: 'rgba(56, 56, 56, 0.1)'
};

const PaymentPlanInstallmentTable = ({ installments = [], isLoading }) => (
  <AppStack style={{ gap: 5, overflow: 'auto' }}>
    <Table withColumnBorders withRowBorders withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <Table.Th style={HEADER_STYLE}>
            <AppFlexbox style={{ gap: 16, alignItems: 'center' }}>
              <AppText style={{ fontSize: 14, fontWeight: 500 }}>Date</AppText>
            </AppFlexbox>
          </Table.Th>

          <Table.Th style={HEADER_STYLE}>
            <AppFlexbox style={{ gap: 16, alignItems: 'center' }}>
              <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                Amount
              </AppText>
            </AppFlexbox>
          </Table.Th>

          <Table.Th style={HEADER_STYLE}>
            <AppFlexbox style={{ gap: 16, alignItems: 'center' }}>
              <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                Status
              </AppText>
            </AppFlexbox>
          </Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {isLoading ? (
          [...Array(3)].map((_, index) => (
            <Table.Tr
              // eslint-disable-next-line react/no-array-index-key
              key={index}
            >
              <Table.Td style={{ padding: '8px 16px' }}>
                <Skeleton height={12} width={110} />
              </Table.Td>
              <Table.Td style={{ padding: '8px 16px' }}>
                <Skeleton height={12} width={70} />
              </Table.Td>
              <Table.Td style={{ padding: '8px 16px' }}>
                <Skeleton height={12} width={80} />
              </Table.Td>
            </Table.Tr>
          ))
        ) : installments.length === 0 ? (
          <Table.Tr>
            <Table.Td colSpan={3} style={{ padding: 40, textAlign: 'center' }}>
              <AppText style={{ fontSize: 14 }} weight={500}>
                - No scheduled payments -
              </AppText>
            </Table.Td>
          </Table.Tr>
        ) : (
          [...installments]
            .sort(
              (a, b) =>
                new Date(a.dueDate) - new Date(b.dueDate) || a.sort - b.sort
            )
            .map((installment) => {
              const status = getInstallmentStatusInfo(
                installment.fkWebsitePaymentStatus
              );

              return (
                <Table.Tr key={installment.pkRegCartPaymentInstallment}>
                  <Table.Td style={{ padding: '8px 16px' }}>
                    <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                      <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                        {dayjs(formatLocalDate(installment.dueDate)).format(
                          'MMM D, YYYY'
                        )}
                      </AppText>
                      {installment.isPastDue &&
                        status.value !== WEBSITE_PAYMENT_STATUS_ENUM.FAILED && (
                          <AppText
                            style={{
                              fontSize: 12,
                              color: PROCESSING_COLOR,
                              whiteSpace: 'nowrap'
                            }}
                            weight={500}
                          >
                            Processing
                          </AppText>
                        )}
                    </AppFlexbox>
                  </Table.Td>

                  <Table.Td style={{ padding: '8px 16px' }}>
                    <AppText style={{ fontSize: 14, fontWeight: 'normal' }}>
                      <NumberFormat
                        decimalScale={2}
                        displayType="text"
                        fixedDecimalScale
                        prefix="$"
                        thousandSeparator
                        value={installment.amountDueInCents / 100}
                      />
                    </AppText>
                  </Table.Td>

                  <Table.Td style={{ padding: '8px 16px' }}>
                    <AppText
                      style={{
                        fontSize: 14,
                        fontWeight: 'normal',
                        color: status.color
                      }}
                    >
                      {status.label}
                    </AppText>
                  </Table.Td>
                </Table.Tr>
              );
            })
        )}
      </Table.Tbody>
    </Table>
  </AppStack>
);

PaymentPlanInstallmentTable.propTypes = {
  installments: PropTypes.array,
  isLoading: PropTypes.bool
};

export default PaymentPlanInstallmentTable;
